<template>
  <div class="maintenance-body" :class="{ mobile: isMobile, vertical: isMobileVertical, landscape: isMobileLandscape }">
    <section class="container">
      <div class="content">
        <div class="modal-header-wrapper">
          <Logo />
        </div>
        <h1>We'll be back soon!</h1>
        <p class="subtitle">Our system is currently undergoing scheduled maintenance.</p>
        <div class="time-section">
          <p><strong>From:</strong> {{ maintenance.from }}</p>
          <p><strong>To:</strong> {{ maintenance.to }}</p>
        </div>
        <button class="try-again-button" @click="debouncedReloadPage" :disabled="isLoading">
          {{ isLoading ? 'Checking...' : 'Try Again' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import Logo from '@/components/logo.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { isMobile } from 'mobile-device-detect';
import { Orientation } from '@/utils/index.model';
import { getItemFromStore } from '@/utils/helpers';
import { getGameRouteName } from '@/utils/constant';
import { debounce } from 'lodash';

export default {
  components: {
    Logo,
  },
  data() {
    return {
      isMobile,
      Orientation,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters('general', ['maintenance']),
    isMobileLandscape() {
      return this.isMobile && this.$screen.orientation === Orientation.landscape;
    },
    isMobileVertical() {
      return this.isMobile && this.$screen.orientation === Orientation.portrait;
    },
  },
  created() {
    this.debouncedReloadPage = debounce(this.reloadPage, 2000, {
      leading: true,
      trailing: false,
    });
  },
  methods: {
    ...mapActions('general', ['getMaintenanceStatus']),
    async reloadPage() {
      if (this.isLoading) return;

      try {
        this.isLoading = true;
        await this.getMaintenanceStatus();
        if (!this.maintenance.m) {
          const { token, clientVersion } = getItemFromStore('general').general;
          if (token) {
            this.$router.push({ name: getGameRouteName(clientVersion || 1) });
          } else {
            this.$router.push({ name: 'launch' });
          }
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    // Clean up the debounced function
    if (this.debouncedReloadPage) {
      this.debouncedReloadPage.cancel();
    }
  },
};
</script>

<style scoped lang="scss">
.maintenance-body {
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  margin: auto;
  position: relative;
  background-color: #0a0d14;

  &.mobile {
    &.vertical {
      .container {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 16px;
        height: calc(100% - 72px);
      }

      .content {
        max-width: 100%;
        padding: 16px;
      }
    }

    &.landscape {
      .container {
        padding: 8px;
      }

      .content {
        max-width: 90%;
      }
    }
  }

  .container {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    padding: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-image: url('~@/assets/images/cf_bg.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: right bottom;
    background-color: #0a0d14;
    overflow-y: auto;
  }

  .content {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .modal-header-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 16px;
    }

    img {
      max-width: 80%;
      /* Responsive logo sizing */
      height: auto;
      margin-bottom: 16px;
      filter: brightness(1.2);
      /* Enhance visibility for light-colored logos */
    }

    h1 {
      font-size: 24px;
      font-weight: bold;
      color: #ffffff;
      /* White for contrast */
      margin-bottom: 8px;
    }

    .subtitle {
      font-size: 16px;
      color: #b0b0b0;
      /* Light gray for subtitle */
      margin-bottom: 16px;
    }

    .time-section {
      margin-bottom: 16px;
      font-size: 14px;
      color: #e0e0e0;

      /* Light text for readability */
      p {
        margin: 8px 0;

        strong {
          color: #ffffff;
          /* White for emphasis */
        }
      }
    }

    .try-again-button {
      background-image: linear-gradient(358deg, #007bff 99%, #0056b3 1%);
      border: none;
      color: #ffffff;
      padding: 10px 24px;
      font-size: 14px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-image 0.3s ease;

      &:hover:not(:disabled) {
        background-image: linear-gradient(358deg, #0056b3 99%, #003d80 1%);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-image: linear-gradient(358deg, #6c757d 99%, #5a6268 1%);
      }
    }
  }
}
</style>
